import { DatavizTypeAI } from "./dataviz";

type AISummaryMap = {
  "audience-analysis": AudienceAnalysisSummary;
  "general-analysis": GeneralAnalysisSummary;
  "risk-analysis": RiskAnalysisSummary;
  "signal-analysis": SignalAnalysisSummary;
};

export type AISummaries =
  | AudienceAnalysisSummary
  | GeneralAnalysisSummary
  | RiskAnalysisSummary
  | SignalAnalysisSummary;

export type AISummary<T extends DatavizTypeAI> = AISummaryMap[T];

export type GeneralAnalysisSummary = {
  topReach: SubAnalysisSummary;
  topEngagement: SubAnalysisSummary;
  latestNews: SubAnalysisSummary;
  negativeAnalysis: SubAnalysisSummary; // null if "no negative analysis" on call
};

export type RiskAnalysisSummary = {
  latestNews: SubAnalysisSummary;
  mostVisible: SubAnalysisSummary;
  mostEngaging: SubAnalysisSummary;
  emerging: SubAnalysisSummary;
};

export type AudienceAnalysisSummary = {
  countries: SubAnalysisSummary;
  occupations: SubAnalysisSummary;
  users: SubAnalysisSummary;
};

export type SignalAnalysisSummary = {
  hashtags: SubAnalysisSummary;
  emotions: SubAnalysisSummary;
  affects: SubAnalysisSummary;
  mentions: SubAnalysisSummary;
};

export enum FailureReasons {
  NotEnoughData = "NOT_ENOUGH_POSTS",
  AIFailure = "SUMMARY_GENERATION_ISSUE",
}

export type FailureReason =
  | FailureReasons.NotEnoughData
  | FailureReasons.AIFailure;

type FailedSubAnalysisSummary = {
  success: false;
  reason: FailureReason;
};

type SucessfulSubAnalysisSummary = {
  success: true;
  summary: string;
};

export type SubAnalysisSummary =
  | FailedSubAnalysisSummary
  | SucessfulSubAnalysisSummary;

export const isGeneralAnalysis = (
  data: AISummaries,
  datavizType: DatavizTypeAI
): data is GeneralAnalysisSummary => {
  return datavizType === "general-analysis";
};

export const isRiskAnalysis = (
  data: AISummaries,
  datavizType: DatavizTypeAI
): data is RiskAnalysisSummary => {
  return datavizType === "risk-analysis";
};

export const isSignalAnalysis = (
  data: AISummaries,
  datavizType: DatavizTypeAI
): data is SignalAnalysisSummary => {
  return datavizType === "signal-analysis";
};

export const isAudienceAnalysis = (
  data: AISummaries,
  datavizType: DatavizTypeAI
): data is AudienceAnalysisSummary => {
  return datavizType === "audience-analysis";
};

export const isSucessfulSubAnalysisSummary = (
  subAnalysisSummary: SubAnalysisSummary
): subAnalysisSummary is SucessfulSubAnalysisSummary => {
  return subAnalysisSummary.success;
};

export const isFailedSubAnalysisSummary = (
  subAnalysisSummary: SubAnalysisSummary
): subAnalysisSummary is FailedSubAnalysisSummary => {
  return !subAnalysisSummary.success;
};
